// Chrome AI utility functions for chat functionality

import { ChromeAIStatus, LanguageModelSession, ChatMessage, ImageFile } from '@/types/chrome-ai';

const MULTIMODAL_EXPECTED_INPUTS = [
  { type: 'text', languages: ['en'] },
  { type: 'image' },
] as const;

const MULTIMODAL_EXPECTED_OUTPUTS = [
  { type: 'text', languages: ['en'] },
] as const;

export interface AISessionConfig {
  systemPrompt: string;
  temperature?: number;
  topK?: number;
  enableMultimodal?: boolean;
}

/**
 * Check if Chrome's built-in AI is available and ready to use
 */
export async function checkChromeAIAvailability(): Promise<ChromeAIStatus> {
  if (typeof window === 'undefined') {
    return {
      available: false,
      status: 'Window object unavailable',
      instructions: 'Run inside a Chrome browser environment',
      promptApiAvailable: false,
      languageModelAvailable: false,
    };
  }

  const LanguageModel = (window as any).LanguageModel;

  if (!LanguageModel?.availability) {
    return {
      available: false,
      status: 'Chrome LanguageModel not detected',
      instructions: 'Enable these Chrome flags:\n• chrome://flags/#prompt-api-for-gemini-nano\n• chrome://flags/#prompt-api-for-gemini-nano-multimodal-input\n• chrome://flags/#optimization-guide-on-device-model',
      promptApiAvailable: false,
      languageModelAvailable: false,
    };
  }

  const languageModelAvailable = true;

  try {
    // Check availability with multimodal options for image support
    const availability = await LanguageModel.availability({
      expectedInputs: MULTIMODAL_EXPECTED_INPUTS,
      expectedOutputs: MULTIMODAL_EXPECTED_OUTPUTS,
    });
    
    if (availability === 'available') {
      return {
        available: true,
        status: 'Ready',
        promptApiAvailable: true,
        languageModelAvailable,
      };
    }

    if (availability === 'after-download') {
      return {
        available: false,
        status: 'Gemini Nano model is downloading',
        instructions: 'Keep Chrome open until the model download completes, then try again.',
        promptApiAvailable: false,
        languageModelAvailable,
      };
    }

    if (availability === 'no') {
      return {
        available: false,
        status: 'This device does not support the Gemini Nano model',
        instructions: 'Your device hardware may not be compatible with Chrome\'s built-in AI.',
        promptApiAvailable: false,
        languageModelAvailable,
      };
    }

    return {
      available: false,
      status: `Chrome Prompt API not ready (status: ${availability})`,
      instructions: 'Please ensure Chrome AI flags are properly enabled and restart Chrome.',
      promptApiAvailable: false,
      languageModelAvailable,
    };
  } catch (error) {
    return {
      available: false,
      status: 'Error checking availability',
      instructions: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      promptApiAvailable: false,
      languageModelAvailable,
    };
  }
}

/**
 * Create a new Chrome AI session for chat
 */
export async function createChatSession(config?: AISessionConfig): Promise<LanguageModelSession> {
  // Check if LanguageModel is available in window
  if (!('LanguageModel' in window)) {
    throw new Error('LanguageModel not available in window');
  }

  const LanguageModel = (window as any).LanguageModel;

  if (!LanguageModel?.availability || !LanguageModel.create) {
    throw new Error('Chrome LanguageModel API not available');
  }

  // Per docs, always pass the same options to availability() that you'll use in create()/prompt()
  const availability = await LanguageModel.availability({
    expectedInputs: MULTIMODAL_EXPECTED_INPUTS,
    expectedOutputs: MULTIMODAL_EXPECTED_OUTPUTS,
  });
  
  if (availability !== 'available') {
    if (availability === 'after-download') {
      throw new Error('Chrome is downloading the Gemini Nano model. Keep this tab open and try again shortly.');
    }
    if (availability === 'no') {
      throw new Error('This device does not support the Gemini Nano model.');
    }
    throw new Error(`Chrome Prompt API not ready (status: ${availability}).`);
  }

  // Use provided config or defaults
  const sessionConfig: any = {
    initialPrompts: [
      { 
        role: 'system', 
        content: config?.systemPrompt || `You are a helpful AI assistant. Please provide clear, concise, and accurate responses. Be conversational and friendly while maintaining professionalism.`
      }
    ]
  };

  // Add multimodal support if enabled (default: true)
  if (config?.enableMultimodal !== false) {
    sessionConfig.expectedInputs = MULTIMODAL_EXPECTED_INPUTS;
    sessionConfig.expectedOutputs = MULTIMODAL_EXPECTED_OUTPUTS;
  }

  // Only add temperature and topK if both are provided (Chrome AI requirement)
  if (config?.temperature !== undefined && config?.topK !== undefined) {
    sessionConfig.temperature = config.temperature;
    sessionConfig.topK = config.topK;
  }

  const session = await LanguageModel.create(sessionConfig);

  return session;
}

/**
 * Send a message to the Chrome AI and get a response
 */
export async function sendMessage(session: LanguageModelSession, message: string, images?: ImageFile[]): Promise<string> {
  try {
    // If we have files/images, use session.append() first, then prompt
    if (images && images.length > 0) {
      const content: Array<{ type: 'text' | 'image'; value: string | File }> = [
        {
          type: 'text' as const,
          value: message || 'Please analyze these files.'
        }
      ];

      // Add each file to the content array
      for (const imageFile of images) {
        // For images, use 'image' type; for other files, use 'text' type with file content
        if (imageFile.file.type.startsWith('image/')) {
          content.push({
            type: 'image' as const,
            value: imageFile.file
          });
        } else {
          // For non-image files, try to read as text or pass the file directly
          content.push({
            type: 'image' as const, // Chrome AI might handle non-image files through image type
            value: imageFile.file
          });
        }
      }

      // Append the multimodal content to the session
      await session.append([
        {
          role: 'user',
          content: content
        }
      ]);

      // Create a more specific prompt based on file types
      const hasImages = images.some(f => f.file.type.startsWith('image/'));
      const hasDocs = images.some(f => !f.file.type.startsWith('image/'));
      
      let promptText = 'Please provide your analysis.';
      if (hasImages && hasDocs) {
        promptText = 'Please analyze both the images and documents I uploaded.';
      } else if (hasImages) {
        promptText = 'Please analyze the images I uploaded.';
      } else if (hasDocs) {
        promptText = 'Please analyze the documents/files I uploaded.';
      }
      
      const response = await session.prompt(promptText);
      return typeof response === 'string' ? response : String(response);
    } else {
      // For text-only messages, use prompt directly
      const response = await session.prompt(message);
      return typeof response === 'string' ? response : String(response);
    }
  } catch (error) {
    console.error('Error sending message to Chrome AI:', error);
    throw new Error(`Failed to get response: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Clean up the AI session
 */
export function destroySession(session: LanguageModelSession | null): void {
  if (session && typeof session.destroy === 'function') {
    try {
      session.destroy();
    } catch (error) {
      console.warn('Error destroying AI session:', error);
    }
  }
}

/**
 * Generate a unique ID for chat messages
 */
export function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create a new chat message
 */
export function createChatMessage(role: 'user' | 'assistant', content: string, images?: ImageFile[]): ChatMessage {
  return {
    id: generateMessageId(),
    role,
    content,
    images: images?.map(img => img.file),
    timestamp: new Date(),
  };
}

/**
 * Create an ImageFile from a File object
 */
export function createImageFile(file: File): ImageFile {
  return {
    file,
    id: generateMessageId(),
    preview: URL.createObjectURL(file),
    name: file.name,
    size: file.size,
    type: file.type,
  };
}

/**
 * Validate if file is a supported type for AI analysis
 */
export function isValidImageFile(file: File): boolean {
  const supportedTypes = [
    // Images
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml',
    // Documents
    'application/pdf', 'text/plain', 'text/markdown', 'text/csv',
    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    // Code files
    'text/javascript', 'text/typescript', 'text/html', 'text/css', 'application/json',
    'text/x-python', 'text/x-java-source', 'text/x-c', 'text/x-c++',
  ];
  return supportedTypes.includes(file.type) && file.size <= 50 * 1024 * 1024; // 50MB limit
}

/**
 * Clean up image preview URLs
 */
export function cleanupImagePreviews(images: ImageFile[]): void {
  images.forEach(img => {
    if (img.preview) {
      URL.revokeObjectURL(img.preview);
    }
  });
}